import React, { useEffect, useState } from 'react';
import MainLayout from 'src/components/layouts/MainLayout';
import styles from 'src/styles/layout.module.css';
import localStyles from 'src/components/pages/Company/CompanyPage/CompanyPage.module.css';
import { Breadcrumb, Button, Layout, Space, Typography } from 'antd';
import { Link, NavLink, Redirect, Route, Switch, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useCompanyById } from 'src/features/companies/companies.hooks';
import { fetchCompany } from 'src/features/companies/companies.state';
import { Company } from 'src/features/companies/companies.types';
import {
  AppstoreOutlined,
  CheckSquareOutlined,
  CustomerServiceOutlined,
  DollarTwoTone,
  FileDoneOutlined,
  FileTextTwoTone,
  InboxOutlined,
  PictureOutlined,
  SettingTwoTone,
  ShopTwoTone,
  ThunderboltTwoTone,
  UserOutlined,
  VideoCameraOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import CompanyOffersTab from 'src/components/pages/Company/CompanyPage/CompanyOffersTab';
import CompanyPackagesTab from 'src/components/pages/Company/CompanyPage/CompanyPackagesTab';
import CompanyAccountsTab from 'src/components/pages/Company/CompanyPage/CompanyAccountsTab';
import CompanyTemplatesTab from './CompanyTemplatesTab/CompanyTemplatesTab';
import { fetchLocations } from 'src/features/locations/locations.state';
import { AppDispatch } from 'src/index';
import CompanySettingsTab from 'src/components/pages/Company/CompanyPage/CompanySettingsTab';
import CompanyDevicesTab from 'src/components/pages/Company/CompanyPage/CompanyDevicesTab';
import RouteProtected from 'src/components/blocks/RouteProtected/RouteProtected';
import CompanyBillingTab from 'src/components/pages/Company/CompanyPage/CompanyBillingTab';
import CompanyDetailsTab from 'src/components/pages/Company/CompanyPage/CompanyDetailsTab';
import Loader from 'src/components/atoms/Loader';
import CompanyBannersTab from 'src/components/pages/Company/CompanyPage/CompanyBannersTab/CompanyBannersTab';
import CompanyParametersTab from 'src/components/pages/Company/CompanyPage/CompanyParametersTab';
import CompanyVideosTab from 'src/components/pages/Company/CompanyPage/CompanyVideosTab';
import { RoleEnum } from 'src/features/users/users.types';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';
import CompanyDeviceScreensTab from './CompanyDeviceScreensTab';
import CompanyCmdTab from './CompanyCmdTab';
import CompanyServicesTab from './CompanyServicesTab';
import CompanyAdvTab from 'src/components/pages/Company/CompanyPage/CompanyAdvTabs/CompanyAdvTabs';
import { GroupOutlined, SearchOutlined } from '@ant-design/icons';
import CompanyWifiTab from './CompanyWifiTab';
import CompanyHotelAddressTab from 'src/components/pages/Company/CompanyPage/CompanyAddressTab/CompanyAddressTab';

const { Content } = Layout;

const CompanyPage: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const dispatch: AppDispatch = useDispatch();
  const company = useCompanyById(companyId) as Company;

  useEffect(() => {
    dispatch(fetchLocations({}));
  }, [companyId, dispatch]);

  return (
    <MainLayout>
      <div>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/commercial/companies'>
                <Space>
                  <ShopTwoTone />
                  Компании
                </Space>
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{company.name}</Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Typography.Title level={2} className={styles.title}>
            {company.name}
          </Typography.Title>
          <Space size='small' wrap>
            <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.observer]}>
              <NavLink to={`billing`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <DollarTwoTone />
                  Биллинг
                </Button>
              </NavLink>
              <NavLink to={`devices`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <ThunderboltTwoTone />
                  Девайсы
                </Button>
              </NavLink>
            </ComponentRoleProtected>
            <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}>
              <NavLink to={`videos`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <VideoCameraOutlined />
                  Видео
                </Button>
              </NavLink>
              <NavLink to={`banners`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <PictureOutlined />
                  Баннеры
                </Button>
              </NavLink>
              <NavLink to={`adv`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <PictureOutlined />
                  Реклама
                </Button>
              </NavLink>
              <NavLink to={`packages`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <InboxOutlined />
                  Пакеты
                </Button>
              </NavLink>
              <NavLink to={`screens`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <AppstoreOutlined />
                  Экраны
                </Button>
              </NavLink>
              <NavLink to={`services`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <CustomerServiceOutlined />
                  Сервисы
                </Button>
              </NavLink>
            </ComponentRoleProtected>
            <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.observer]}>
              <NavLink to={`accounts`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <UserOutlined />
                  Аккаунты
                </Button>
              </NavLink>
              <NavLink to={`offers`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <FileDoneOutlined />
                  Оферты
                </Button>
              </NavLink>
              <NavLink to={`cmd`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <FileDoneOutlined />
                  Команды
                </Button>
              </NavLink>
              <NavLink to={`wifi`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <CheckSquareOutlined />
                  wifi
                </Button>
              </NavLink>
              <NavLink to={`parameters`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <CheckSquareOutlined />
                  Параметры
                </Button>
              </NavLink>
              <NavLink to={`settings`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <SettingTwoTone />
                  Настройки
                </Button>
              </NavLink>
              <NavLink to={`templates`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <GroupOutlined />
                  Шаблоны
                </Button>
              </NavLink>
              <NavLink to={`address`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <EnvironmentOutlined />
                  Адреса отелей
                </Button>
              </NavLink>
              <NavLink to={`details`} activeClassName={styles.activeLink}>
                <Button size='middle'>
                  <FileTextTwoTone />О Компании
                </Button>
              </NavLink>
            </ComponentRoleProtected>
          </Space>
          <Content>
            <Route
              render={({ match: { path } }) => (
                <Switch>
                  <RouteProtected
                    exact
                    path={`${path}/screens`}
                    component={CompanyDeviceScreensTab}
                    roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/services`}
                    component={CompanyServicesTab}
                    roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/accounts`}
                    component={CompanyAccountsTab}
                    roles={[RoleEnum.admin, RoleEnum.observer]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/banners`}
                    component={CompanyBannersTab}
                    roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/videos`}
                    component={CompanyVideosTab}
                    roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/adv`}
                    component={CompanyAdvTab}
                    roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/settings`}
                    component={CompanySettingsTab}
                    roles={[RoleEnum.admin, RoleEnum.observer]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/wifi`}
                    component={CompanyWifiTab}
                    roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/templates`}
                    component={CompanyTemplatesTab}
                    roles={[RoleEnum.admin, RoleEnum.observer]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/address`}
                    component={CompanyHotelAddressTab}
                    roles={[RoleEnum.admin, RoleEnum.observer]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/details`}
                    component={CompanyDetailsTab}
                    roles={[RoleEnum.admin, RoleEnum.observer]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/packages`}
                    component={CompanyPackagesTab}
                    roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/offers`}
                    component={CompanyOffersTab}
                    roles={[RoleEnum.admin, RoleEnum.observer]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/cmd`}
                    component={CompanyCmdTab}
                    roles={[RoleEnum.admin, RoleEnum.observer]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/devices`}
                    component={CompanyDevicesTab}
                    roles={[RoleEnum.admin, RoleEnum.observer]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/billing`}
                    component={CompanyBillingTab}
                    roles={[RoleEnum.admin, RoleEnum.observer]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/parameters`}
                    component={CompanyParametersTab}
                    roles={[RoleEnum.admin, RoleEnum.observer]}
                  />
                  <Redirect from={path} to={`${path}/videos`} exact />
                </Switch>
              )}
            />
          </Content>
        </div>
      </div>
    </MainLayout>
  );
};

const CompanyPageBlock: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const dispatch: AppDispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      await dispatch(fetchCompany({ companyId }));
      setLoading(false);
    })();
  }, [companyId, dispatch]);

  if (loading) {
    return <Loader />;
  }

  return <CompanyPage />;
};

export default CompanyPageBlock;
